import { computed, Ref } from "vue";
import { DateTime } from "luxon";
import { BookingForMap, DriverPositionUpdateModalParameters } from "@components/DriverPositionUpdateModalPlugin";

// ─── Colour palette (shared between map rendering and UI components) ───────────
export const PALETTE = ['#007bff','#28a745','#6f42c1','#fd7e14','#20c997','#e83e8c','#17a2b8','#ffc107','#dc3545','#343a40'];
export const DEAD_COLOR = '#6c757d';
export const bookingColor = (i: number) => PALETTE[i % PALETTE.length];

// ─── Pure math ─────────────────────────────────────────────────────────────────
export const haversine = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371, dLat = (lat2 - lat1) * Math.PI / 180, dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

export const haversineSum = (pts: App.Models.DriverPositionUpdate[]): number =>
    pts.reduce((d, p, i) => i === 0 ? 0 : d + haversine(pts[i - 1].latitude, pts[i - 1].longitude, p.latitude, p.longitude), 0);

// ─── Accuracy helpers (pure, usable in components and composables) ─────────────
export const accuracyPct = (gps: number, odo: number | null): number | null => {
    if (odo === null || odo <= 0) return null;
    return Math.round((gps / odo) * 100);
};

export const accuracyClass = (pct: number): string => {
    if (pct > 110) return 'info';
    if (pct >= 90)  return 'success';
    if (pct >= 75)  return 'warning';
    return 'danger';
};

export const accuracyLabel = (pct: number): string => {
    if (pct > 110) return `Overcounting (${pct}%)`;
    if (pct >= 90)  return `Good (${pct}%)`;
    if (pct >= 75)  return `Partial (${pct}%)`;
    return `Low (${pct}%)`;
};

// ─── Formatting helpers ────────────────────────────────────────────────────────
export const formatTime = (iso: string | null | undefined): string =>
    iso ? DateTime.fromISO(iso).toFormat('HH:mm:ss') : '—';

export const segmentDuration = (pts: App.Models.DriverPositionUpdate[]): string => {
    if (pts.length < 2) return '—';
    const dur = DateTime.fromISO(pts[pts.length - 1].position_recorded_at)
        .diff(DateTime.fromISO(pts[0].position_recorded_at));
    const h = Math.floor(dur.as('hours')), m = Math.floor(dur.as('minutes')) % 60, s = Math.floor(dur.as('seconds')) % 60;
    return [h && `${h}h`, m && `${m}m`, (!h && !m) || s ? `${s}s` : ''].filter(Boolean).join(' ');
};

// ─── Composable ────────────────────────────────────────────────────────────────
export function useGpsAnalytics(
    allPoints: Ref<App.Models.DriverPositionUpdate[]>,
    parameters: Ref<DriverPositionUpdateModalParameters>,
) {
    const totalGpsDistance = computed(() => haversineSum(allPoints.value));

    const odometerSpan = computed((): number | null => {
        const { start_odometer, end_odometer } = parameters.value.journeyOdometers ?? {};
        if (start_odometer == null || end_odometer == null) return null;
        return end_odometer - start_odometer;
    });

    const journeyAccuracyPct = computed(() => accuracyPct(totalGpsDistance.value, odometerSpan.value));

    const odometerDistance = computed((): number | null => {
        const { origin_odometer, destination_odometer } = parameters.value.booking ?? {};
        if (origin_odometer == null || destination_odometer == null) return null;
        return destination_odometer - origin_odometer;
    });

    const significantGaps = computed(() => {
        const THRESHOLD_MS = 5 * 60 * 1000;
        const bookings = parameters.value.journeyBookings ?? [];
        return allPoints.value
            .flatMap((p, i) => {
                if (i === 0) return [];
                const prev = allPoints.value[i - 1];
                const gapMs = DateTime.fromISO(p.position_recorded_at).toMillis()
                            - DateTime.fromISO(prev.position_recorded_at).toMillis();
                if (gapMs < THRESHOLD_MS) return [];
                const midMs = DateTime.fromISO(prev.position_recorded_at).toMillis() + gapMs / 2;
                let segment = 'Dead running';
                for (let bi = 0; bi < bookings.length; bi++) {
                    const pu  = bookings[bi].actual_pick_up_time  ? DateTime.fromISO(bookings[bi].actual_pick_up_time!).toMillis()  : null;
                    const do_ = bookings[bi].actual_drop_off_time ? DateTime.fromISO(bookings[bi].actual_drop_off_time!).toMillis() : null;
                    if (pu && do_ && midMs >= pu && midMs <= do_) {
                        segment = `During booking — ${bookings[bi].clientName ?? 'Booking ' + (bi + 1)}`;
                        break;
                    }
                }
                return [{ fromTime: formatTime(prev.position_recorded_at), toTime: formatTime(p.position_recorded_at), durationMin: Math.round(gapMs / 60000), segment }];
            })
            .sort((a, b) => b.durationMin - a.durationMin);
    });

    const bookingGpsStats = computed(() =>
        (parameters.value.journeyBookings ?? []).map((booking, i) => {
            const from = booking.actual_pick_up_time  ? DateTime.fromISO(booking.actual_pick_up_time).toMillis()  : null;
            const to   = booking.actual_drop_off_time ? DateTime.fromISO(booking.actual_drop_off_time).toMillis() + 5 * 60 * 1000 : null;
            const points = (from !== null && to !== null)
                ? allPoints.value.filter(p => { const t = DateTime.fromISO(p.position_recorded_at).toMillis(); return t >= from && t <= to; })
                : [];
            const gpsDistance = haversineSum(points);
            const odometerDistance = (booking.origin_odometer != null && booking.destination_odometer != null)
                ? booking.destination_odometer - booking.origin_odometer : null;
            return { booking, i, points, gpsDistance, odometerDistance, bookingAccuracyPct: accuracyPct(gpsDistance, odometerDistance) };
        })
    );

    return { totalGpsDistance, odometerSpan, journeyAccuracyPct, odometerDistance, significantGaps, bookingGpsStats };
}
